import type { FC, HTMLAttributes, ReactNode } from 'react'
import React from 'react'
import styles from './stepper.module.css'

export interface StepperProps extends HTMLAttributes<HTMLDivElement> {
  activeStep?: number
  orientation?: 'horizontal' | 'vertical'
  connector?: ReactNode
}

const orientationMap = {
  horizontal: styles.horizontal,
  vertical: styles.vertical,
} as const

export const Stepper: FC<StepperProps> = ({
  activeStep = 0,
  orientation = 'horizontal',
  connector,
  className,
  children,
  style,
  ...rest
}) => {
  const classes = [styles.stepper, orientationMap[orientation], className].filter(Boolean).join(' ')

  const steps = React.Children.toArray(children)

  return (
    <div className={classes} style={style} {...rest}>
      {steps.map((child, index) => (
        <React.Fragment key={index}>
          {React.isValidElement<StepProps>(child)
            ? React.cloneElement(child, {
                'data-step-index': index,
                'data-step-completed': index < activeStep || child.props.completed ? '' : undefined,
                'aria-current': index === activeStep || child.props.active ? 'step' : undefined,
              } as Record<string, unknown>)
            : child}
          {index < steps.length - 1 ? (connector ?? <StepConnector />) : null}
        </React.Fragment>
      ))}
    </div>
  )
}

export interface StepProps extends HTMLAttributes<HTMLDivElement> {
  active?: boolean
  completed?: boolean
  disabled?: boolean
  ref?: React.Ref<HTMLDivElement>
}

export const Step: FC<StepProps> = ({ active, completed, disabled, className, children, ref, ...rest }) => {
  const classes = [
    styles.step,
    active ? styles.stepActive : undefined,
    completed ? styles.stepCompleted : undefined,
    disabled ? styles.stepDisabled : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={ref} className={classes} {...rest}>
      {children}
    </div>
  )
}

export interface StepButtonProps extends HTMLAttributes<HTMLButtonElement> {
  /** @deprecated Ignored - kept for MUI compat */
  color?: string
}

export const StepButton: FC<StepButtonProps> = ({ color: _color, className, children, ...rest }) => {
  const classes = [styles.stepButton, className].filter(Boolean).join(' ')

  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  )
}

export interface StepLabelProps extends HTMLAttributes<HTMLDivElement> {
  icon?: ReactNode
  slots?: {
    stepIcon?: FC<Record<string, unknown>>
  }
}

export const StepLabel: FC<StepLabelProps> = ({ icon, slots, className, children, ...rest }) => {
  const classes = [styles.stepLabel, className].filter(Boolean).join(' ')
  const StepIcon = slots?.stepIcon

  return (
    <div className={classes} {...rest}>
      {StepIcon ? (
        <span className={styles.stepIcon}>
          <StepIcon />
        </span>
      ) : icon ? (
        <span className={styles.stepIcon}>{icon}</span>
      ) : null}
      <span className={styles.stepLabelText}>{children}</span>
    </div>
  )
}

export interface StepContentProps extends HTMLAttributes<HTMLDivElement> {}

export const StepContent: FC<StepContentProps> = ({ className, children, ...rest }) => {
  const classes = [styles.stepContent, className].filter(Boolean).join(' ')

  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  )
}

export interface StepConnectorProps extends HTMLAttributes<HTMLDivElement> {}

export const StepConnector: FC<StepConnectorProps> = ({ className, style, ...rest }) => {
  const classes = [styles.connector, className].filter(Boolean).join(' ')

  return <div className={classes} style={style} {...rest} />
}
